import { motion } from "framer-motion";
import { Cpu, Bot, Stethoscope, Eye } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const IMGS = [
  // AI Systems — iconic Google data center ceiling shot (Unsplash, Joshua Sortino)
  "https://images.unsplash.com/photo-1488229297570-58520851e868?auto=format&fit=crop&w=1600&q=80",
  // Robotics — modern humanoid robot in clean studio (intuitive, recognizable)
  "https://images.unsplash.com/photo-1717347424091-08275b73c918?auto=format&fit=crop&w=1600&q=80",
  // Medical Devices — pulse oximeter close-up (Unsplash, Jair Lázaro)
  "https://images.unsplash.com/photo-1513224502586-d1e602410265?auto=format&fit=crop&w=1600&q=80",
  // HCI — researcher at workstation, user research / interaction observation
  "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1600&q=80",
];
const ICONS = [Cpu, Bot, Stethoscope, Eye];

export default function Domains() {
  const { t } = useLang();
  const D = t.domains;
  return (
    <section
      id="domains"
      data-testid="domains-section"
      className="relative border-b border-white/10 py-28 lg:py-36"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 lg:col-span-4">
            <div className="label-mono mb-4">{D.label}</div>
            <h2 className="display-2 text-4xl md:text-5xl text-white">
              {D.title_a}
              <br />
              <span className="text-white/40">{D.title_b}</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6 self-end">
            <p className="text-white/70 text-base leading-relaxed max-w-xl">{D.intro}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border border-white/10">
          {D.cards.map((d, i) => {
            const Icon = ICONS[i];
            const n = String(i + 1).padStart(2, "0");
            return (
              <motion.article
                key={n}
                data-testid={`domain-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`group relative overflow-hidden border-white/10 ${
                  i % 2 === 0 ? "md:border-r" : ""
                } ${i < 2 ? "border-b" : ""} bg-[#101012] hover-tilt`}
              >
                <div className="relative h-72 w-full overflow-hidden">
                  <img
                    src={IMGS[i]}
                    alt={d.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: "grayscale(0.8) contrast(1.05) brightness(0.55)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101012] via-[#101012]/40 to-transparent" />
                  <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                    <span className="font-mono text-[10px] tracking-widest uppercase text-white/70">
                      {D.domain_word} · {n}
                    </span>
                    <div className="w-9 h-9 border border-white/30 group-hover:border-[#FF3333] group-hover:bg-[#FF3333] transition-colors flex items-center justify-center">
                      <Icon size={16} className="text-white" />
                    </div>
                  </div>
                  <h3 className="absolute bottom-5 left-5 right-5 font-display font-bold text-3xl text-white tracking-tight">
                    {d.title}
                  </h3>
                </div>

                <div className="p-8">
                  <p className="text-white/65 text-sm leading-relaxed">{d.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {d.tags.map((tg) => (
                      <span
                        key={tg}
                        className="font-mono text-[10px] tracking-widest uppercase text-white/50 border border-white/15 px-3 py-1.5"
                      >
                        {tg}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
